import { getAccessToken } from './getAccessToken';
import moment from 'moment';

const environmentUrl = "https://orgea61b289.crm8.dynamics.com";

function normalizeGuid(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return String(value).replace(/[{}]/g, '');
}

export interface BackingCodingData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string | null;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string | null;
  cr3ea_platform: string;
  cr3ea_machineproof: string | null;
  cr3ea_remarks: string | null;
  cr3ea_sku: string | null;
  cr3ea_productname: string;
  cr3ea_executivename: string;
  cr3ea_attachments: string | null;
}

export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * Collect data for one product row for Backing Coding save.
 * Multiple products per cycle: call once per product; each call persists one record.
 */
export function collectBackingCodingRecord(
  cycleNum: number,
  product: string,
  executiveName: string,
  skuVal: string | null,
  proofVal: string | null,
  remarksVal: string | null,
  QualityTourId: string,
  UserName: string | null,
  cr3ea_attachments: string | null
): BackingCodingData {
  return {
    cr3ea_qualitytourid: normalizeGuid(QualityTourId),
    cr3ea_title: "CodeVerification_" + moment().format("MM-DD-YYYY"),
    cr3ea_cycle: `Cycle-${cycleNum}`,
    cr3ea_shift: sessionStorage.getItem("shiftValue") || "shift 1",
    cr3ea_tourstartdate: moment().format("MM-DD-YYYY"),
    cr3ea_observedby: UserName || null,
    cr3ea_platform: "PWA",
    cr3ea_machineproof: proofVal || null,
    cr3ea_remarks: remarksVal || null,
    cr3ea_sku: skuVal || null,
    cr3ea_productname: product || "N/A",
    cr3ea_executivename: executiveName || "N/A",
    cr3ea_attachments: cr3ea_attachments,
  };
}

/**
 * Save section API call - inserts into cr3ea_prod_codings.
 */
export async function savesectionApicall(
  data: BackingCodingData[],
  optionalAccessToken?: string
): Promise<SaveResponse> {
  try {
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = true);

    let accessToken: string | undefined = optionalAccessToken;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_codings";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results: any[] = [];
    for (const record of data) {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(record),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      const result = await response.json();
      results.push(result);
    }

    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results,
    };
  } catch (error) {
    console.error("Error saving Backing Coding records:", error);
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to save records. Please try again.",
      data: [],
    };
  }
}

export interface CycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  sku: string;
  machineProof: string;
  remarks: string;
}

/**
 * Fetch completed cycles for Backing Coding by QualityTourId.
 */
export async function fetchCycleData(
  QualityTourId: string,
  optionalAccessToken?: string | null
): Promise<CycleData[]> {
  try {
    let accessToken: string | undefined = optionalAccessToken ?? undefined;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_codings";
    const qualityTourGuid = normalizeGuid(QualityTourId);

    const selectFields = [
      "cr3ea_cycle",
      "cr3ea_productname",
      "cr3ea_executivename",
      "cr3ea_sku",
      "cr3ea_machineproof",
      "cr3ea_remarks",
      "cr3ea_attachments",
    ].join(",");

    const buildUrl = (filter: string) =>
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}` +
      `?$filter=${encodeURIComponent(filter)}` +
      `&$select=${selectFields}`;

    let filter = `cr3ea_qualitytourid eq '${qualityTourGuid}'`;
    let response = await fetch(buildUrl(filter), { headers });

    if (!response.ok) {
      const errText = await response.text();
      if (errText.includes("Unrecognized") || errText.includes("Edm.") || errText.includes("$filter")) {
        filter = `_cr3ea_qualitytourid_value eq '${qualityTourGuid}'`;
        response = await fetch(buildUrl(filter), { headers });
        if (!response.ok) {
          throw new Error(`Failed to fetch data (retry): ${response.status} - ${await response.text()}`);
        }
      } else {
        throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
      }
    }

    const data = await response.json();
    const cycles: CycleData[] = [];

    (data.value || []).forEach((record: any) => {
      const cycleNum = (record.cr3ea_cycle || "").replace("Cycle-", "").trim();
      if (!cycleNum) return;

      cycles.push({
        cycleNum,
        product: record?.cr3ea_productname ?? "",
        executiveName: record?.cr3ea_executivename ?? "",
        sku: record?.cr3ea_sku ?? "",
        machineProof: record?.cr3ea_machineproof ?? "",
        remarks: record?.cr3ea_remarks ?? "",
      });
    });

    return cycles;
  } catch (error) {
    console.error("Error fetching Backing Coding cycle data:", error);
    return [];
  }
}
